import { useState } from "react";
import { Outlet, Link } from "react-router-dom";
import { Sparkles, ArrowRight, Menu, Store, X, ChevronRight, MapPin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { APP_NAME } from "@/lib/constants";
import { get } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import { useUIStore } from "@/store/ui-store";
import { BottomNav } from "./BottomNav";
import { Sidebar } from "./Sidebar";
import type { Agent } from "@/types/agent";
import { agentApi } from "@/lib/extensions-api";

export function PublicLayout() {
  const { isAuthenticated } = useAuthStore();
  const { toggleMobileMenu } = useUIStore();

  // Controls the Right Sidebar Drawer (Vendors & Info)
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);

  // Fetch active vendors for the right drawer
  const { data: vendors } = useQuery({
    queryKey: ["active-vendors"],
    queryFn: agentApi.publicVendors,
  });

  return (
    <div className={`min-h-screen bg-surface-50 flex flex-col relative ${isAuthenticated ? "pb-16 lg:pb-0" : ""}`}>

      {/* 🌟 OVERLAY DRAWERS */}
      <Sidebar /> {/* Left Drawer (Controlled by Zustand UI Store) */}

      {/* Right Drawer Backdrop */}
      {rightDrawerOpen && (
        <div className="fixed inset-0 z-40 bg-surface-900/60 backdrop-blur-sm transition-opacity" onClick={() => setRightDrawerOpen(false)} />
      )}

      {/* Right Drawer (Vendors & Support) */}
      <aside className={`fixed top-0 right-0 z-50 h-screen w-80 bg-white border-l border-surface-200 shadow-2xl flex flex-col transition-transform duration-300 ${rightDrawerOpen ? "translate-x-0" : "translate-x-full"}`}>
        <div className="h-16 flex items-center justify-between px-5 border-b border-surface-200">
          <h2 className="font-bold text-surface-900 flex items-center gap-2"><Store className="w-5 h-5 text-brand-600" /> Official Shops</h2>
          <button onClick={() => setRightDrawerOpen(false)} className="p-2 rounded-lg text-surface-500 hover:bg-surface-100 transition-colors"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <ul className="p-4 space-y-3">
            {vendors?.filter(v => v.shop_name).map(vendor => (
              <li key={vendor.id}>
                <Link to={`/vendor/${vendor.id}`} onClick={() => setRightDrawerOpen(false)} className="group flex items-center justify-between p-3 rounded-xl bg-surface-50 border border-surface-100 hover:border-brand-300 hover:bg-brand-50 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    {vendor.shop_logo ? (
                      <img src={vendor.shop_logo} alt={vendor.shop_name} className="w-10 h-10 rounded-lg object-cover bg-white" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-surface-200 text-surface-500 flex items-center justify-center font-bold">{vendor.shop_name.charAt(0)}</div>
                    )}
                    <div>
                      <div className="text-sm font-bold text-surface-900 leading-tight group-hover:text-brand-700 transition-colors">{vendor.shop_name}</div>
                      <div className="text-[10px] text-surface-500 flex items-center gap-1 mt-0.5"><MapPin className="w-2.5 h-2.5" /> Verified Agent</div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-surface-300 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all" />
                </Link>
              </li>
            ))}
          </ul>

          <div className="p-4 border-t border-surface-100 mt-2">
            <h3 className="text-xs font-bold uppercase tracking-widest text-surface-400 mb-3">Support Links</h3>
            <ul className="space-y-1">
              <li><Link to="#" className="block p-2.5 rounded-lg hover:bg-surface-50 text-surface-700 hover:text-brand-600 font-medium text-sm">Terms & Conditions</Link></li>
              <li><Link to="#" className="block p-2.5 rounded-lg hover:bg-surface-50 text-surface-700 hover:text-brand-600 font-medium text-sm">FAQ</Link></li>
              <li><Link to="#" className="block p-2.5 rounded-lg hover:bg-surface-50 text-surface-700 hover:text-brand-600 font-medium text-sm">Contact Us</Link></li>
            </ul>
          </div>
        </div>
      </aside>

      {/* 🌟 PUBLIC NAVBAR */}
      <header className="h-16 bg-white/80 backdrop-blur-xl border-b border-surface-200 sticky top-0 z-30 px-4 sm:px-6 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4 sm:gap-6">
          {/* Left Drawer Menu Button */}
          {isAuthenticated && (
            <button onClick={toggleMobileMenu} className="p-2 -ml-2 rounded-lg text-surface-600 hover:bg-surface-100 transition-colors">
              <Menu className="h-6 w-6" />
            </button>
          )}

          <Link to="/" className="flex items-center gap-2">
            {/* 🛠️ FIXED: Your custom logo goes here */}
            <img
              src="/logo.png"
              alt="Meher Shop"
              className="h-8 w-auto object-contain shrink-0"
            />
          </Link>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Right Drawer Button (Stores) */}
          <button onClick={() => setRightDrawerOpen(true)} className="flex items-center gap-2 px-3 h-10 rounded-xl bg-surface-100 hover:bg-surface-200 text-surface-700 font-bold text-sm transition-colors mr-2">
            <Store className="w-4 h-4" /> <span className="hidden sm:inline">Shops</span>
          </button>

          {isAuthenticated ? (
            <Link to="/dashboard">
              <Button className="shadow-brand-500/25 h-10 px-4 sm:px-6 rounded-xl hidden sm:flex" rightIcon={<ArrowRight className="w-4 h-4" />}>
                Dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" className="font-bold text-surface-600 hover:bg-surface-100 hover:text-surface-900 h-10 px-5 rounded-xl">Sign In</Button>
              </Link>
              <Link to="/register"><Button className="shadow-brand-500/25 font-bold h-10 px-5 rounded-xl">Join Now</Button></Link>
            </>
          )}
        </div>
      </header>

      {/* 🌟 MAIN CONTENT (FULL WIDTH) */}
      <main className="flex-1 w-full overflow-x-hidden">
        <Outlet />
      </main>

      {/* 🌟 PUBLIC FOOTER */}
      <footer className="bg-surface-950 text-surface-400 pt-16 pb-8 text-center text-sm relative z-20">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-10 text-center md:text-left mb-12">
          {/* ... footer content remains the same ... */}
          <div className="flex flex-col items-center md:items-start">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-surface-800 grid place-items-center"><Sparkles className="h-4 w-4 text-surface-300" /></div>
              <span className="font-extrabold text-lg text-white tracking-tight">{APP_NAME}</span>
            </div>
            <p className="text-surface-500 leading-relaxed max-w-xs">The most exclusive auto club delivering premium products and lifetime rewards.</p>
          </div>
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-bold text-white mb-4 uppercase tracking-widest text-xs">Quick Links</h3>
            <ul className="space-y-3 text-surface-500 font-medium">
              <li><Link to="/catalog" className="hover:text-brand-400 transition-colors">Browse Catalog</Link></li>
              <li><button onClick={() => setRightDrawerOpen(true)} className="hover:text-brand-400 transition-colors">Official Shops</button></li>
              <li><Link to="/login" className="hover:text-brand-400 transition-colors">Member Login</Link></li>
            </ul>
          </div>
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-bold text-white mb-4 uppercase tracking-widest text-xs">Support</h3>
            <ul className="space-y-3 text-surface-500 font-medium">
              <li><a href="#" className="hover:text-brand-400 transition-colors">Terms & Conditions</a></li>
              <li><a href="#" className="hover:text-brand-400 transition-colors">Contact Us</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-surface-800/50 pt-8 flex flex-col items-center gap-2 px-6">
          <p className="text-surface-500">© {new Date().getFullYear()} {APP_NAME}. All rights reserved.</p>
        </div>
      </footer>

      {isAuthenticated && <BottomNav />}
    </div>
  );
}